import * as CSS from 'csstype'
import * as React from 'react'

import type {ForceOmit} from '../shared'
import {compile} from '../styles'

const isa = Array.isArray

export interface SVGProps
  extends
    HasSize,
    Pick<CSS.SvgProperties, 'fill' | 'fillRule' | 'stroke' | 'strokeWidth' | 'strokeLinecap' | 'strokeLinejoin'> {
  style?: React.CSSProperties
  children?: React.ReactNode
  className?: string
  refProxy?: React.Ref<any>
  key?: React.Key
}

type PresizedSVGStyle = ForceOmit<React.CSSProperties, 'width' | 'height' | 'color' | 'fill' | 'stroke'>

type FlexibleSVGStyle = ForceOmit<React.CSSProperties, 'color' | 'fill' | 'stroke'>

export interface GeneratedSVGProps<S extends Partial<React.CSSProperties>> {
  style?: S
  className?: string
  color?: CSS.Property.Color
}

export interface HasSize {
  size: Dimensions
}

interface HasPath {
  path: string | string[]
}

interface Translate {
  x: number
  y: number
}

interface Dimensions {
  height: number
  width: number
}

interface Rotate {
  angle: number
  x: number
  y: number
}

export type FillRule = 'nonzero' | 'evenodd'
export type StrokeLinecap = 'butt' | 'square' | 'round'
export type StrokeLinejoin = 'miter' | 'bevel' | 'round'

type In = 'SourceGraphic' | 'SourceAlpha' | 'BackgroundImage' | 'BackgroundAlpha' | 'FillPaint' | 'StrokePaint' | string
type FilterUnits = 'userSpaceOnUse' | 'objectBoundingBox'
type Type = 'matrix' | 'saturate' | 'hueRotate' | 'luminanceToAlpha'

export interface Common {
  id?: string
  children?: React.ReactNode
  translate?: Translate
  rotate?: Rotate
}

export interface Drawing extends Common {
  fill?: string
  stroke?: string
  strokeWidth?: number
  fillRule?: FillRule
}

export interface Path extends Drawing {
  d: string
}

export interface Filter extends Common {
  x?: string
  y?: string
  width?: string
  height?: string
  filterUnits?: FilterUnits
}

export interface FeOffset extends Common {
  dx?: string
  dy?: string
  result?: string
  in?: In
}

export interface FeGaussianBlur extends Common {
  stdDeviation?: string
  result?: string
  in?: In
}

export interface FeColorMatrix extends Common {
  values?: string
  type?: Type
  in?: In
}

export interface Use extends Common {
  href?: string
  filter?: string
}

export interface G extends Common {
  mask?: string
  stroke?: string
  strokeWidth?: CSS.Property.StrokeWidth
  fill?: string
  fillOpacity?: string
  strokeLinecap?: StrokeLinecap
  strokeLinejoin?: StrokeLinejoin
}

export interface Circle extends G {
  r: number
  cx?: number
  cy?: number
  strokeDasharray?: number
  strokeDashoffset?: number
}

// !!!WARN!!! forces "display: block" to avoid spurious margins and make this behave more like mobile svg
// see https://stackoverflow.com/questions/22337292/spurious-margin-on-svg-element
const SvgDefaultStyle = {display: 'block'}

export const Svg = (p: SVGProps) => {
  const {
    children,
    refProxy,
    key,
    className,
    style,
    size,
    fill,
    fillRule,
    stroke,
    strokeWidth,
    strokeLinecap,
    strokeLinejoin
  } = p

  const props = {
    ref: refProxy,
    viewBox: `0 0 ${size.width} ${size.height}`,
    fillRule,
    key,
    className,
    fill,
    stroke,
    strokeWidth,
    strokeLinecap,
    strokeLinejoin,
    style: compile([SvgDefaultStyle, size, style])
  }

  if (children) {
    return isa(children) ? React.createElement('svg', props, ...children) : React.createElement('svg', props, children)
  }
  return React.createElement('svg', props)
}

export const Circle = (p: Circle) => {
  const {
    r,
    cx,
    cy,
    mask,
    fill,
    fillOpacity,
    stroke,
    strokeWidth,
    strokeDasharray,
    strokeDashoffset,
    translate,
    rotate
  } = p
  const props = {
    r,
    cx,
    cy,
    mask,
    fill,
    stroke,
    strokeWidth,
    fillOpacity,
    strokeDasharray,
    strokeDashoffset,
    transform: toTransform(translate, rotate)
  }

  return React.createElement('circle', props)
}

export const G = (p: G) => {
  const {mask, fill, fillOpacity, stroke, strokeWidth, strokeLinecap, strokeLinejoin, translate, rotate, children} = p
  const props = {
    mask,
    fill,
    stroke,
    strokeWidth,
    fillOpacity,
    strokeLinecap,
    strokeLinejoin,
    transform: toTransform(translate, rotate)
  }

  if (children) {
    return isa(children) ? React.createElement('g', props, ...children) : React.createElement('g', props, children)
  }
  return React.createElement('g', props)
}

export const Path = (p: Path) => {
  const {children, id, d, fill, stroke, strokeWidth, translate, rotate} = p
  const props = {
    id,
    d,
    fill,
    stroke,
    strokeWidth,
    transform: toTransform(translate, rotate)
  }

  if (children) {
    return isa(children)
      ? React.createElement('path', props, ...children)
      : React.createElement('path', props, children)
  }
  return React.createElement('path', props)
}

export const Defs = (p: Common) => {
  const {translate, rotate, children} = p
  const props = {
    transform: toTransform(translate, rotate)
  }

  if (children) {
    return isa(children)
      ? React.createElement('defs', props, ...children)
      : React.createElement('defs', props, children)
  }
  return React.createElement('defs', props)
}

export const Filter = (p: Filter) => {
  const {id, x, y, width, height, filterUnits, translate, rotate, children} = p
  const props = {
    id,
    x,
    y,
    width,
    height,
    filterUnits,
    transform: toTransform(translate, rotate)
  }

  if (children) {
    return isa(children)
      ? React.createElement('filter', props, ...children)
      : React.createElement('filter', props, children)
  }
  return React.createElement('filter', props)
}

export const FeOffset = (p: FeOffset) => {
  const {dx, dy, result, translate, rotate, children} = p

  const props = {
    dx,
    dy,
    result,
    in: p.in,
    transform: toTransform(translate, rotate)
  }

  if (children) {
    return isa(children)
      ? React.createElement('feOffset', props, ...children)
      : React.createElement('feOffset', props, children)
  }
  return React.createElement('feOffset', props)
}

export const FeGaussianBlur = (p: FeGaussianBlur) => {
  const {stdDeviation, result, translate, rotate, children} = p

  const props = {
    stdDeviation,
    result,
    in: p.in,
    transform: toTransform(translate, rotate)
  }

  if (children) {
    return isa(children)
      ? React.createElement('feGaussianBlur', props, ...children)
      : React.createElement('feGaussianBlur', props, children)
  }
  return React.createElement('feGaussianBlur', props)
}

export const FeColorMatrix = (p: FeColorMatrix) => {
  const {values, type, translate, rotate, children} = p

  const props = {
    values,
    type,
    in: p.in,
    transform: toTransform(translate, rotate)
  }

  if (children) {
    return isa(children)
      ? React.createElement('feColorMatrix', props, ...children)
      : React.createElement('feColorMatrix', props, children)
  }
  return React.createElement('feColorMatrix', props)
}

export const Mask = (p: Drawing) => {
  const {id, fill, translate, rotate, children} = p

  const props = {
    id,
    fill,
    transform: toTransform(translate, rotate)
  }

  if (children) {
    return isa(children)
      ? React.createElement('mask', props, ...children)
      : React.createElement('mask', props, children)
  }
  return React.createElement('mask', props)
}

export const Use = (p: Use) => {
  const {href, filter, translate, rotate, children} = p

  const props = {
    href,
    filter,
    transform: toTransform(translate, rotate)
  }

  if (children) {
    return isa(children) ? React.createElement('use', props, ...children) : React.createElement('use', props, children)
  }
  return React.createElement('use', props)
}

const addTranslate = (a: string[], _?: Translate) => {
  if (_) {
    a.push(`translate(${_.x}, ${_.y})`)
  }
}

const addRotate = (a: string[], _?: Rotate) => {
  if (_) {
    a.push(`rotate(${_.angle}, ${_.x}, ${_.y})`)
  }
}

const toTransform = (translate?: Translate, rotate?: Rotate) => {
  const transforms: string[] = []

  addTranslate(transforms, translate)
  addRotate(transforms, rotate)

  return transforms.length > 0 ? transforms.join(' ') : undefined
}

const SVGSize = {
  small: {height: 16, width: 16},
  medium: {height: 20, width: 20},
  large: {height: 24, width: 24},
  xlarge: {height: 32, width: 32}
}

export function SmallFilled({path}: HasPath) {
  return Filled({
    path,
    size: SVGSize.small
  })
}

export function MediumFilled({path}: HasPath) {
  return Filled({
    path,
    size: SVGSize.medium
  })
}

export function LargeFilled({path}: HasPath) {
  return Filled({
    path,
    size: SVGSize.large
  })
}

export function SmallStroked({path}: HasPath) {
  return Stroked({
    path,
    size: SVGSize.small
  })
}

export function SmallStrokedFlexible({path}: HasPath) {
  return Stroked<FlexibleSVGStyle>({
    path,
    size: SVGSize.small
  })
}

export function MediumStroked({path}: HasPath) {
  return Stroked({
    path,
    size: SVGSize.medium
  })
}

export function LargeStroked({path}: HasPath) {
  return Stroked({
    path,
    size: SVGSize.large
  })
}

export function XLargeStroked({path}: HasPath) {
  return Stroked({
    path,
    size: SVGSize.xlarge
  })
}

function Filled<S extends React.CSSProperties = PresizedSVGStyle>({
  size,
  path,
  fillRule
}: HasPath & HasSize & Pick<CSS.SvgProperties, 'fillRule'>) {
  const children = typeof path === 'string' ? Path({d: path}) : path.map((d) => Path({d}))
  return function Filled(props: GeneratedSVGProps<S>) {
    const {color, style, className} = props
    return Svg({
      className: ['filled', className].filter((item) => !!item).join(' '),
      style,
      size,
      fill: color,
      fillRule,
      children
    })
  }
}

const strokeWidth = '1.25px'

function Stroked<S extends React.CSSProperties = PresizedSVGStyle>({size, path}: HasPath & HasSize) {
  const children = typeof path === 'string' ? Path({d: path}) : path.map((d) => Path({d}))
  return function Stroked(props: GeneratedSVGProps<S>) {
    const {color, style, className} = props
    return Svg({
      className: ['stroked', className].filter((item) => !!item).join(' '),
      size,
      fill: 'none',
      stroke: color,
      strokeWidth,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      style,
      children
    })
  }
}
